import React from "react";
import { useNavigate } from "react-router-dom";
import {
  getProjection,
  getHeadlineAsIs,
  improvementPct,
  collectSubprocessRows,
  splitAppliedRows,
  getFallbackChildren,
  DEFAULT_INCLUDE_SUBPROCESS,
} from "../../utils/subprocessRedesign";
import { ChildChip, ProjectionBanner, FallbackChildrenNote } from "./SubprocessProjectionBits";
import {
  getNvaElimination,
  getKpiBasis,
  getKpiHeadline,
  getHeadlineMetric,
  isNvaDrivenRow,
  hasAnyChange,
} from "../../utils/nvaElimination";
import { NvaEliminationCard, NvaSourceChip, KpiBasisNote } from "./NvaEliminationBits";

// goal values the RL API accepts, per RL_OPTIMIZATION_FRONTEND_API.md §3.
// Exported so the BPMN tab's "RL BPMN" view can label the last run's goal
// with the same words shown here.
export const GOAL_OPTIONS = [
  { key: "both", label: "Balanced", hint: "50/50 time & cost — recommended default" },
  { key: "time", label: "Faster", hint: "Minimise cycle time, will spend money to do it" },
  { key: "cost", label: "Cheaper", hint: "Minimise labour cost" },
];

// §6 of the API doc.
const STOP_REASON_MESSAGES = {
  non_positive_q_value: "No further improvements found.",
  no_qualifying_heuristics: "All applicable improvements have been applied.",
  max_steps_reached: "Reached the maximum of 8 changes.",
  no_improvement_found: "No safe improvement available; leaving the process unchanged.",
};

const SECTION_STYLE = {
  applied: { bg: "var(--green-tint-100)", ink: "var(--green-ink-800)" },
  recommended: { bg: "var(--yellow-tint-100)", ink: "var(--yellow-ink-800)" },
  rule_not_met: { bg: "var(--slate-100)", ink: "var(--slate-600)" },
};

function dqChipsSmall(affects) {
  return ["time", "cost", "quality", "flexibility"]
    .filter((k) => affects && affects[k])
    .map((k) => (
      <span key={k} style={{ fontSize: 9, fontWeight: 700, color: "var(--slate-600)", background: "var(--slate-100)", borderRadius: 999, padding: "1px 6px", marginRight: 3, textTransform: "capitalize" }}>{k}</span>
    ));
}

// Green ▲ for an improvement, red ▼ otherwise — matches the arrow/color
// convention used by the AS-IS vs TO-BE tab elsewhere on this page.
function pctBadge(v) {
  if (v == null) return "—";
  const improved = v > 0;
  const color = v === 0 ? "#6b7280" : improved ? "#059669" : "#dc2626";
  const glyph = v === 0 ? "–" : improved ? "▲" : "▼";
  return <span style={{ fontWeight: 800, color }}>{glyph} {Math.abs(v).toFixed(1)}%</span>;
}

// Marks a row whose AS-IS figure comes from the projected baseline rather than
// from projection.true_as_is (which only carries cycle time and resource cost).
function ProjectedBaselineMark() {
  return (
    <span title="AS-IS for this row is the projected baseline, not the current state" style={{ color: "var(--violet-ink-600)", cursor: "help" }}>
      {" "}†
    </span>
  );
}

/**
 * Renders the RL agent's own redesign (POST /cms/rl-optimize/{id}) — a
 * separate pipeline from the deterministic /cms/optimize heuristics matrix
 * rendered alongside it. Goal-first, on-demand: checks readiness via the
 * preview endpoint, then only trains once the analyst picks a goal and runs it.
 *
 * RL state (preview/result/etc.) is owned by the page (useRlOptimizationData
 * lifted there) and passed in as props, not fetched locally — the last run's
 * final_bpmn_xml needs to survive this panel unmounting when the analyst
 * switches to the BPMN tab's "RL BPMN" view.
 */
export default function RlRedesignPanel({
  processId, money, num2, fmtMin, fixDecimals,
  preview, previewLoading, previewError, loadPreview,
  result, running, runError, run,
  onViewBpmn,
}) {
  const navigate = useNavigate();
  const [goal, setGoal] = React.useState("both");
  // On by default (see DEFAULT_INCLUDE_SUBPROCESS): a parent whose time sits inside
  // its children reports "no change justified" without it. Costs one extra training
  // run per sub-process.
  const [includeSubprocesses, setIncludeSubprocesses] = React.useState(DEFAULT_INCLUDE_SUBPROCESS);

  if (!processId) return null;

  if (previewLoading) {
    return <div className="no-results">Checking whether this process is ready for RL optimization…</div>;
  }

  if (previewError) {
    return (
      <div style={{ padding: "12px 16px", background: "var(--red-tint-50)", border: "1px solid var(--red-tint-300)", borderRadius: 10, color: "var(--red-ink-800)", fontSize: 13 }}>
        <strong>Couldn&apos;t check RL readiness:</strong> {previewError}{" "}
        <button type="button" className="secondary-btn sm" style={{ marginLeft: 8 }} onClick={() => loadPreview(processId)}>Retry</button>
      </div>
    );
  }

  if (!preview) return null;

  if (!preview.ready) {
    return (
      <div style={{ padding: "12px 16px", background: "var(--amber-tint-50)", border: "1px solid var(--amber-tint-200)", borderRadius: 10, color: "var(--amber-ink-800)", fontSize: 13 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>This process isn&apos;t ready for RL optimization yet.</div>
        {(preview.validation?.issues || []).map((iss, i) => (
          <div key={i} style={{ marginTop: 4 }}>
            <span style={{ fontWeight: 700 }}>{iss.severity}:</span> {iss.message}{iss.element_name ? ` (${iss.element_name})` : ""}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Goal</span>
        {GOAL_OPTIONS.map((g) => (
          <button
            key={g.key}
            type="button"
            className={`opt-tab-btn ${goal === g.key ? "active" : ""}`}
            style={{ padding: "4px 14px", fontSize: 12 }}
            aria-pressed={goal === g.key}
            title={g.hint}
            onClick={() => setGoal(g.key)}
          >
            {g.label}
          </button>
        ))}
        <button
          type="button"
          className="primary-btn sm"
          style={{ marginLeft: 8 }}
          disabled={running}
          onClick={() => run(processId, goal, { includeSubprocessRedesigns: includeSubprocesses })}
        >
          {running ? "Training the RL agent…" : result ? "Re-run" : "Run RL Optimization"}
        </button>
        <span style={{ fontSize: 11, color: "var(--gray-400)" }}>seed: 42 (reproducible)</span>
      </div>

      <label
        style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, fontSize: 12, color: "var(--gray-700)", cursor: "pointer" }}
        title="Sub-processes are frozen during a normal run, so a parent whose time mostly sits inside its children reports no change. This redesigns them too and prices the effect into this process."
      >
        <input
          type="checkbox"
          checked={includeSubprocesses}
          disabled={running}
          onChange={(e) => setIncludeSubprocesses(e.target.checked)}
        />
        <span>Also redesign this process&apos;s sub-processes</span>
        <span style={{ fontSize: 11, color: "var(--gray-400)" }}>
          adds ~5–15s per sub-process · results are proposals, not saved to CMS
        </span>
      </label>

      {running && (
        <div className="no-results">
          {includeSubprocesses
            ? "Training the RL agent on this process and each of its sub-processes (typically 15–60s)…"
            : "Training the RL agent on this process (typically 4–15s)…"}
        </div>
      )}

      {runError && (
        <div style={{ padding: "12px 16px", marginBottom: 14, background: "var(--red-tint-50)", border: "1px solid var(--red-tint-300)", borderRadius: 10, color: "var(--red-ink-800)", fontSize: 13 }}>
          <strong>RL optimization failed:</strong> {runError}
        </div>
      )}

      {!result && !running && !runError && (
        <>
          {/* The preview carries the same nva_elimination block as the run, so the
              analyst can see which steps will be dropped before spending a training
              run — and is not surprised by a TO-BE that is missing a task. */}
          <PreRunNvaNotice nva={getNvaElimination(preview)} />
          <div className="no-results">
            AS-IS: {fmtMin(preview.as_is?.cycle_time)} · {money(preview.as_is?.resource_cost)} — pick a goal above and run the RL agent to see a redesign.
          </div>
        </>
      )}

      {result && (
        <RlResult
          result={result}
          navigate={navigate}
          money={money}
          num2={num2}
          fmtMin={fmtMin}
          fixDecimals={fixDecimals}
          onViewBpmn={onViewBpmn}
        />
      )}
    </div>
  );
}

// Compact, forward-looking version of the NVA block for the not-yet-run state. The
// full NvaEliminationCard is written in the past tense and carries measured figures
// the preview does not have.
function PreRunNvaNotice({ nva }) {
  const eliminated = Array.isArray(nva?.eliminated) ? nva.eliminated : [];
  if (eliminated.length === 0) return null;
  return (
    <div style={{ marginBottom: 12, padding: "8px 12px", background: "var(--sky-tint-50)", border: "1px solid var(--sky-tint-200)", borderRadius: 10, color: "var(--sky-ink-800)", fontSize: 12 }}>
      <NvaSourceChip />{" "}
      {eliminated.length} step{eliminated.length === 1 ? "" : "s"} classified non-value-adding in CMS
      {" "}({eliminated.map((t) => t.task_name || `Task ${t.task_id}`).join(", ")}){" "}
      will be removed before the agent searches, so the TO-BE will already exclude
      {eliminated.length === 1 ? " it" : " them"}.
    </div>
  );
}

/**
 * Heading for one child's block of heuristics. Carries the two figures that are
 * easy to confuse and must never be shown as one number: what the redesign does
 * inside the child, and what it does to THIS process. They diverge whenever the
 * child sits behind a gateway, so they are labelled separately rather than
 * presented as a single "saving".
 */
function ChildGroupHeader({ group, fmtMin, money }) {
  const { childAsIs, childToBe, parentImpact } = group;
  const internal = childAsIs?.cycle_time != null && childToBe?.cycle_time != null;
  const saved = parentImpact?.cycle_time_saved;
  const costSaved = parentImpact?.resource_cost_saved;

  return (
    <tr>
      <td colSpan={5} style={{ padding: "8px 10px", background: "var(--purple-tint-50)", borderTop: "1px solid var(--violet-tint-100)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <ChildChip name={group.childProcessName} />
          <span style={{ fontSize: 10.5, color: "var(--gray-400)" }}>
            {group.rows.length} heuristic{group.rows.length === 1 ? "" : "s"} applied inside this sub-process
          </span>
        </div>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 5, fontSize: 11.5 }}>
          <div>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: "var(--gray-400)", textTransform: "uppercase" }}>Within the sub-process</div>
            <div style={{ color: "var(--gray-700)" }}>
              {internal ? `${fmtMin(childAsIs.cycle_time)} → ${fmtMin(childToBe.cycle_time)}` : "—"}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: "var(--gray-400)", textTransform: "uppercase" }}>Effect on this process</div>
            <div style={{ color: saved > 0 ? "var(--green-ink-700)" : "var(--gray-700)", fontWeight: 700 }}>
              {saved != null ? `${fmtMin(saved)} saved` : "—"}
              {costSaved != null && (
                <span style={{ fontWeight: 400, color: "var(--gray-500)" }}> · {money(costSaved)} saved</span>
              )}
            </div>
          </div>
        </div>
        {parentImpact?.note && (
          <div style={{ marginTop: 4, fontSize: 10.5, color: "var(--gray-400)", maxWidth: 640 }}>{parentImpact.note}</div>
        )}
      </td>
    </tr>
  );
}

// One heuristic row. `childName` is set only for source="subprocess" rows, which
// have no `affects` and no decline `reason` — their text is measured_verdict.
function HeuristicRow({ h, sectionKey, idx, fixDecimals, childName }) {
  // Rule N2 — this row is applied, but the agent did not choose it: the steps were
  // classified non-value-adding in CMS and removed before the search began. Rendered
  // in the CMS blue rather than the agent's green so the credit reads correctly at a
  // glance, not only to someone who opens the tooltip.
  const nvaDriven = isNvaDrivenRow(h);
  const accent = childName ? "#7c3aed" : nvaDriven ? "#0284c7" : "#16a34a";
  const bg = childName
    ? "#fdfcff"
    : nvaDriven
      ? "#f0f9ff"
      : sectionKey === "applied"
        ? "#f0fdf4"
        : sectionKey === "recommended"
          ? "#fffdf6"
          : idx % 2
            ? "#fcfcfd"
            : "#fff";
  return (
    <tr style={{ verticalAlign: "top", background: bg }}>
      <td style={{ padding: "8px 10px", fontWeight: 700, whiteSpace: "nowrap", position: "sticky", left: 0, background: "inherit", zIndex: 1, ...(h.implemented ? { boxShadow: `inset 3px 0 0 ${accent}` } : {}) }}>
        {h.implemented && <span aria-hidden="true" style={{ color: accent, fontWeight: 800, marginRight: 4 }}>✓</span>}
        {h.heuristic}
        <div style={{ fontSize: 9.5, fontWeight: 400, color: "var(--gray-400)", textTransform: "uppercase" }}>{String(h.category || "").replace(/_/g, " ")}</div>
        {childName && (
          <div style={{ marginTop: 3 }}>
            <ChildChip name={childName} />
          </div>
        )}
        {nvaDriven && (
          <div style={{ marginTop: 3 }}>
            <NvaSourceChip />
          </div>
        )}
      </td>
      <td style={{ padding: "8px 10px", color: "var(--gray-700)", maxWidth: 260 }}>{h.trigger_rule}</td>
      <td style={{ padding: "8px 10px", color: "var(--gray-500)", maxWidth: 320 }}>{fixDecimals(h.measured_verdict)}</td>
      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{dqChipsSmall(h.affects)}</td>
      <td style={{ padding: "8px 10px", maxWidth: 240 }}>
        <span style={{ fontSize: 10, fontWeight: 800, borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap", background: "var(--green-tint-100)", color: "var(--green-ink-700)", border: "1px solid var(--green-tint-300)" }}>
          measurable now
        </span>
        <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 3 }}>{h.measurable_impact?.explanation}</div>
      </td>
    </tr>
  );
}

function RlResult({ result, navigate, money, fmtMin, fixDecimals, onViewBpmn }) {
  const {
    overall_improvement: oi,
    as_is: asIs,
    to_be: toBe,
    applied_heuristics: applied,
    stop_reason: stopReason,
    heuristic_table: table,
    scenario_options: scenarios,
    goal_source: goalSource,
    engine_parity: parity,
    subprocesses,
    final_bpmn_xml: bpmnXml,
  } = result;

  // Rule A — with the projection present, `as_is` and `to_be` are both the PROJECTED
  // figures (children already redesigned) and are typically equal, so the backend's
  // own overall_improvement reads 0%. The real current state is projection.true_as_is,
  // and the headline must be measured against that. getHeadlineAsIs() collapses to
  // `as_is` when the flag is off, so there is one code path, not two.
  const projection = getProjection(result);
  const headlineAsIs = getHeadlineAsIs(result);

  // The backend now resolves the same question itself in kpi_basis.headline and says
  // which baseline it picked (`basis`) and what is baked in (`includes`). Prefer it;
  // the local computation stays as the fallback for a response that predates it.
  const kpiBasis = getKpiBasis(result);
  const headline = getKpiHeadline(result);
  const headlineTime = getHeadlineMetric(headline, "cycle_time_minutes");
  const headlineCost = getHeadlineMetric(headline, "resource_cost");

  const timePct = headlineTime
    ? headlineTime.reduction_pct
    : projection
      ? improvementPct(headlineAsIs?.cycle_time, toBe?.cycle_time)
      : oi?.time_improvement_pct;
  const costPct = headlineCost
    ? headlineCost.reduction_pct
    : projection
      ? improvementPct(headlineAsIs?.resource_cost, toBe?.resource_cost)
      : oi?.cost_improvement_pct;

  const subprocessRows = collectSubprocessRows(result);
  const fallbackChildren = getFallbackChildren(projection);
  const nva = getNvaElimination(result);
  // Rule N1 — `applied_heuristics` is empty on a run whose entire gain came from the
  // CMS NVA elimination, and on a parent whose improvements all sit inside children.
  // Keying "no change" off it alone renders "No changes were applied" directly above
  // a positive improvement badge and a green Applied row.
  const noChange = !hasAnyChange({
    appliedHeuristics: applied,
    nva,
    subprocessRowCount: subprocessRows.length,
  });

  return (
    <div>
      <ProjectionBanner projection={projection} />

      <FallbackChildrenNote items={fallbackChildren} />

      <NvaEliminationCard nva={nva} money={money} fmtMin={fmtMin} />

      {goalSource === "cms" && (
        <div style={{ marginBottom: 10, fontSize: 12, color: "var(--amber-ink-700)", background: "var(--amber-tint-50)", border: "1px solid var(--amber-tint-200)", borderRadius: 8, padding: "6px 12px" }}>
          This process has its own saved optimization goal in CMS, which overrode the goal selected here.
        </div>
      )}

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase" }}>Cycle time</div>
          <div style={{ fontSize: 18 }}>{pctBadge(timePct)}</div>
          {(headlineTime || projection) && (
            <div style={{ fontSize: 11, color: "var(--gray-500)", marginTop: 2 }}>
              {fmtMin(headlineTime ? headlineTime.as_is : headlineAsIs?.cycle_time)} today →{" "}
              {fmtMin(headlineTime ? headlineTime.to_be : toBe?.cycle_time)}
              {projection ? " projected" : ""}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase" }}>Resource cost</div>
          <div style={{ fontSize: 18 }}>{pctBadge(costPct)}</div>
          {(headlineCost || projection) && (
            <div style={{ fontSize: 11, color: "var(--gray-500)", marginTop: 2 }}>
              {money(headlineCost ? headlineCost.as_is : headlineAsIs?.resource_cost)} today →{" "}
              {money(headlineCost ? headlineCost.to_be : toBe?.resource_cost)}
              {projection ? " projected" : ""}
            </div>
          )}
        </div>
      </div>

      <KpiBasisNote basis={kpiBasis} headline={headline} />

      {/* The parent legitimately applies nothing on a process whose time sits inside
          its children. That is not "no heuristics applied" — it must not read as an
          empty result while the children's heuristics are listed below.
          Suppressed when an NVA elimination ran: a step WAS removed from this
          process, so "no change was justified here" would be false. */}
      {(!applied || applied.length === 0) && !nva && subprocessRows.length > 0 && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "var(--violet-tint-50)", border: "1px solid var(--violet-tint-200)", borderRadius: 10, color: "var(--violet-ink-800)", fontSize: 13 }}>
          <strong>No change was justified in this process itself.</strong> All {subprocessRows.length}{" "}
          improvement{subprocessRows.length === 1 ? "" : "s"} below {subprocessRows.length === 1 ? "was" : "were"}{" "}
          found inside its sub-processes — that is where this process&apos;s time is spent.
        </div>
      )}

      {noChange ? (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "var(--slate-100)", border: "1px solid var(--slate-200)", borderRadius: 10, color: "var(--slate-600)", fontSize: 13 }}>
          {STOP_REASON_MESSAGES[stopReason] || "No changes were applied."}
          {/* Only worth suggesting a manual drill-down when the children weren't
              redesigned in this run — with the flag on, they already were. */}
          {!projection && Array.isArray(subprocesses) && subprocesses.length > 0 && (
            <div style={{ marginTop: 8 }}>
              This process contains {subprocesses.length} sub-process{subprocesses.length > 1 ? "es" : ""} — the improvement may live inside one of them:
              <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {subprocesses.map((sp) => (
                  <button
                    key={sp.child_process_id}
                    type="button"
                    className="secondary-btn sm"
                    onClick={() => navigate(`/process-optimization/results/${sp.child_process_id}`)}
                  >
                    {sp.child_process_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        stopReason && (
          <div style={{ marginBottom: 16, fontSize: 12, color: "var(--gray-500)" }}>{STOP_REASON_MESSAGES[stopReason]}</div>
        )
      )}

      <div style={{ overflowX: "auto", marginBottom: 20 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 480, fontSize: 12 }}>
          <thead>
            <tr>
              {["", projection ? "AS-IS (today)" : "AS-IS", projection ? "TO-BE (projected)" : "TO-BE"].map((h) => (
                <th key={h || "label"} style={{ padding: "6px 10px", fontSize: 10.5, color: "var(--gray-500)", textAlign: h ? "right" : "left", textTransform: "uppercase", borderBottom: "2px solid var(--gray-200)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "6px 10px", fontWeight: 600 }}>Cycle time</td>
              <td style={{ padding: "6px 10px", textAlign: "right" }}>{fmtMin(headlineAsIs?.cycle_time)}</td>
              <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{fmtMin(toBe?.cycle_time)}</td>
            </tr>
            <tr>
              <td style={{ padding: "6px 10px", fontWeight: 600 }}>Cycle-time efficiency{projection && <ProjectedBaselineMark />}</td>
              <td style={{ padding: "6px 10px", textAlign: "right" }}>{asIs?.cycle_time_efficiency != null ? `${(asIs.cycle_time_efficiency * 100).toFixed(1)}%` : "—"}</td>
              <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{toBe?.cycle_time_efficiency != null ? `${(toBe.cycle_time_efficiency * 100).toFixed(1)}%` : "—"}</td>
            </tr>
            <tr>
              <td style={{ padding: "6px 10px", fontWeight: 600 }}>Resource cost <span title="Responsible role only — what the agent optimizes" style={{ color: "var(--gray-400)", cursor: "help" }}>ⓘ</span></td>
              <td style={{ padding: "6px 10px", textAlign: "right" }}>{money(headlineAsIs?.resource_cost)}</td>
              <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{money(toBe?.resource_cost)}</td>
            </tr>
            <tr>
              <td style={{ padding: "6px 10px", fontWeight: 600 }}>RACI cost <span title="All R/A/C/I roles" style={{ color: "var(--gray-400)", cursor: "help" }}>ⓘ</span>{projection && <ProjectedBaselineMark />}</td>
              <td style={{ padding: "6px 10px", textAlign: "right" }}>{money(asIs?.raci_cost)}</td>
              <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{money(toBe?.raci_cost)}</td>
            </tr>
          </tbody>
        </table>
        {projection && (
          // true_as_is carries cycle time and resource cost only, so these two rows
          // still show the projected baseline. Saying so beats quietly mixing the
          // two baselines in one column.
          <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--gray-400)" }}>
            † AS-IS shown for this row is the projected baseline (sub-processes already
            redesigned) — the backend doesn&apos;t return a current-state figure for it.
          </div>
        )}
      </div>

      {parity?.status === "divergent" && (
        <div style={{ marginBottom: 16, fontSize: 12, color: "var(--amber-ink-800)", background: "var(--amber-tint-50)", border: "1px solid var(--amber-tint-200)", borderRadius: 8, padding: "8px 12px" }}>
          <strong>Note:</strong> the RL engine&apos;s own figures diverge from the CMS&apos;s by more than {parity.tolerance_pct}%
          {/* engine_parity compares the headline cost metric, which on this tab is the
              Responsible role only. The Comparison report scores every RACI role and so
              reports a different divergence for the same run — saying which scale this
              percentage is on stops that gap reading as two contradictory numbers. */}
          {parity.cost_delta_pct != null ? ` (${parity.cost_delta_pct.toFixed(1)}% on cost, Responsible role only)` : ""}. {parity.note}
        </div>
      )}

      {Array.isArray(scenarios) && scenarios.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gray-500)", marginBottom: 6 }}>TRADE-OFF OPTIONS</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {scenarios.map((sc, i) => (
              <div key={i} style={{ flex: "1 1 220px", minWidth: 220, border: sc.recommended ? "1.5px solid #16a34a" : "1px solid var(--gray-200)", borderRadius: 10, padding: "10px 14px", background: sc.recommended ? "var(--green-tint-50)" : "var(--color-surface)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{sc.label}</span>
                  {sc.recommended && <span style={{ fontSize: 10, fontWeight: 800, color: "var(--green-ink-700)", background: "var(--green-tint-100)", borderRadius: 999, padding: "1px 8px" }}>RECOMMENDED</span>}
                </div>
                <div style={{ fontSize: 11, color: "var(--gray-500)", margin: "4px 0" }}>{(sc.heuristics || []).join(", ")}</div>
                <div style={{ fontSize: 12, display: "flex", gap: 12 }}>
                  <span>Time {pctBadge(sc.time_improvement_pct)}</span>
                  <span>Cost {pctBadge(sc.cost_improvement_pct)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(table) && table.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gray-500)", marginBottom: 6 }}>
            RL HEURISTIC TRACE — all 9 heuristics the agent considered, grouped by whether they were applied.
            {subprocessRows.length > 0 && (
              <span style={{ fontWeight: 500 }}>
                {" "}Those 9 are this process&apos;s own; heuristics applied inside sub-processes are
                listed under their own headings.
              </span>
            )}
          </div>
          <div style={{ overflowX: "auto", border: "1px solid var(--slate-100)", borderRadius: 10 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900, fontSize: 12 }}>
              <thead>
                <tr>
                  {["Heuristic", "Trigger rule", "Verdict", "DQ dimensions", "Measurable impact"].map((h, i) => (
                    <th key={h} style={{ padding: "8px 10px", fontSize: 10.5, color: "var(--gray-500)", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "2px solid var(--gray-200)", background: "var(--slate-50)", position: i === 0 ? "sticky" : undefined, left: i === 0 ? 0 : undefined, zIndex: i === 0 ? 1 : undefined }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.map((section) => {
                  const style = SECTION_STYLE[section.section] || SECTION_STYLE.rule_not_met;
                  // Sub-process rows are appended after the parent's own within a
                  // section. Grouping them keeps the two from interleaving — a user
                  // must never read a child's heuristic as applied to this process.
                  const { parentRows, childGroups } = splitAppliedRows(section.rows);
                  return (
                    <React.Fragment key={section.section}>
                      <tr>
                        <td colSpan={5} style={{ padding: "7px 10px", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", background: style.bg, color: style.ink, borderTop: "1px solid var(--gray-200)" }}>
                          {section.title}
                          {childGroups.length > 0 && (
                            <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0, opacity: 0.85 }}>
                              {" "}— this process first, then each sub-process
                            </span>
                          )}
                        </td>
                      </tr>

                      {parentRows.map((h, idx) => (
                        <HeuristicRow key={`parent-${h.heuristic}`} h={h} sectionKey={section.section} idx={idx} fixDecimals={fixDecimals} />
                      ))}

                      {parentRows.length === 0 && childGroups.length > 0 && (
                        <tr>
                          <td colSpan={5} style={{ padding: "8px 10px", fontSize: 11.5, color: "var(--gray-500)", fontStyle: "italic", background: "var(--color-surface)" }}>
                            None applied to this process directly.
                          </td>
                        </tr>
                      )}

                      {childGroups.map((g) => (
                        <React.Fragment key={`child-${g.key}`}>
                          <ChildGroupHeader group={g} fmtMin={fmtMin} money={money} />
                          {g.rows.map((h, idx) => (
                            <HeuristicRow
                              key={`child-${g.key}-${h.heuristic}`}
                              h={h}
                              sectionKey={section.section}
                              idx={idx}
                              fixDecimals={fixDecimals}
                              childName={g.childProcessName}
                            />
                          ))}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {bpmnXml && onViewBpmn && (
        <div style={{ padding: "10px 14px", background: "var(--teal-tint-50)", border: "1px solid var(--teal-tint-200)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 12.5, color: "var(--teal-ink-deep)" }}>
            The redesigned diagram for this run ({GOAL_OPTIONS.find((g) => g.key === result.goal)?.label || result.goal}) is ready.
          </span>
          <button type="button" className="secondary-btn sm" onClick={onViewBpmn}>
            View in BPMN tab →
          </button>
        </div>
      )}
    </div>
  );
}
